/**
 * Where Orbis takes a WhatsApp template variable from when an agent picks the template.
 * Shared by the API (validation) and the panel (prefill and the settings editor).
 */
export const TEMPLATE_VARIABLE_SOURCES = [
  "contact_name",
  "contact_phone",
  "contact_email",
  "contact_organization",
  "agent_name",
  "company_name",
  "company_phone",
  "company_address",
  "company_hours",
  "company_website",
  "today",
] as const;
export type TemplateVariableSource = (typeof TEMPLATE_VARIABLE_SOURCES)[number];

export const TEMPLATE_VARIABLE_SOURCE_LABEL: Record<TemplateVariableSource, string> = {
  contact_name: "Nombre del contacto",
  contact_phone: "Teléfono del contacto",
  contact_email: "Correo del contacto",
  contact_organization: "Empresa del contacto",
  agent_name: "Nombre de quien envía",
  company_name: "Nombre de la empresa",
  company_phone: "Teléfono de la empresa",
  company_address: "Dirección de la empresa",
  company_hours: "Horario de atención",
  company_website: "Sitio web de la empresa",
  today: "Fecha de hoy",
};

/** A variable is either read from Orbis data or always the same text. */
export type TemplateVariableBinding = { source: TemplateVariableSource } | { fixed: string };

/** Keyed "body:1", "header:1", "body:cliente"… */
export type TemplateVariableMap = Record<string, TemplateVariableBinding>;

/**
 * Named variables Orbis recognises without any setup: a template written as
 * "Hola {{cliente}}, soy {{asesor}} de {{empresa}}" fills itself.
 */
export const NAMED_TEMPLATE_VARIABLES: Record<string, TemplateVariableSource> = {
  cliente: "contact_name",
  telefono: "contact_phone",
  correo: "contact_email",
  empresa_cliente: "contact_organization",
  asesor: "agent_name",
  empresa: "company_name",
  telefono_empresa: "company_phone",
  direccion: "company_address",
  horario: "company_hours",
  sitio_web: "company_website",
  fecha: "today",
};

export const templateVariableKey = (scope: "body" | "header", name: string) => `${scope}:${name}`;

/** The binding that applies to a variable: the template's own setting, else the named convention. */
export function bindingFor(
  map: TemplateVariableMap,
  scope: "body" | "header",
  name: string,
): TemplateVariableBinding | undefined {
  const own = map[templateVariableKey(scope, name)];
  if (own) return own;
  const source = NAMED_TEMPLATE_VARIABLES[name.toLowerCase()];
  return source ? { source } : undefined;
}
